import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The behaviour both chrome menus share — the team switcher and Docs.
 *
 * README §8: open on click, close on Escape and on outside click, move focus to the first
 * item, arrows move within, Tab leaves. Focus is real DOM focus on the items rather than a
 * painted cursor, so the ring a keyboard user sees is the same ring as everywhere else and a
 * screen reader is told where it is.
 *
 * `autoFocus` is off for exactly one caller: a menu that is *portalled* is placed asynchronously,
 * so on the render that mounts it the box is still at the document's top-left corner and focusing
 * anything inside it scrolls the window to the top of the page. That caller focuses `focusItem`
 * itself once Floating UI has placed the box — see `RowMenu.tsx`.
 *
 * `initialOpen` is for a caller whose *mounting* is what opening means — `AssetMenu`, which the
 * assets grid renders only while a menu is wanted. Such a caller closes by watching `open` go
 * false, and starting closed would have it read `false` on its very first effect and unmount
 * itself before a frame had been drawn.
 */
export function useMenu(itemCount: number, initialIndex = 0, autoFocus = true, initialOpen = false) {
  const [open, setOpen] = useState(initialOpen);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [cursor, setCursor] = useState(initialIndex);

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const focusItem = useCallback((index: number) => {
    setCursor(index);
    const items = menuRef.current?.querySelectorAll<HTMLElement>('[data-menuitem]');
    items?.[index]?.focus();
  }, []);

  // Opening moves focus into the menu. In an effect rather than in the click handler because
  // the items do not exist until the menu has rendered.
  useEffect(() => {
    if (open && autoFocus) focusItem(initialIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function onMenuKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') { event.preventDefault(); close(true); return; }
    // Tab leaves the menu rather than cycling inside it, so the menu closes behind you.
    if (event.key === 'Tab') { setOpen(false); return; }
    if (event.key === 'ArrowDown') { event.preventDefault(); focusItem((cursor + 1) % itemCount); }
    if (event.key === 'ArrowUp') { event.preventDefault(); focusItem((cursor - 1 + itemCount) % itemCount); }
    if (event.key === 'Home') { event.preventDefault(); focusItem(0); }
    if (event.key === 'End') { event.preventDefault(); focusItem(itemCount - 1); }
  }

  function onTriggerKeyDown(event: React.KeyboardEvent) {
    if (!open && (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ')) {
      if (event.key === 'ArrowDown') event.preventDefault();
      setOpen(true);
    }
  }

  return { open, setOpen, close, cursor, setCursor, focusItem, triggerRef, menuRef, onMenuKeyDown, onTriggerKeyDown };
}
